// Kapsül program kataloğu — veri-güdümlü içerik modeli.
// İçerik iki dilli ({ tr, en }) tutulur; sayfalar pick() ile aktif dile göre
// render eder (Etkinlik/Basın içeriğiyle aynı desen). Yeni bir program eklemek
// için buraya bir PROGRAMS girdisi eklemek yeterli — sayfa otomatik üretilir.

import type { Localized } from '@/lib/localized';

export type L = Localized;

export type CategorySlug =
  | 'development'
  | 'support'
  | 'collaboration'
  | 'projects'
  | 'focus'
  | 'events'
  | 'mentorship';

// value: sayı/kısaltma ise düz metin, çevrilmesi gereken bir ifade ise { tr, en }.
export type Stat = { value: string | L; label: L };
export type FeatureItem = { title: L; desc?: L };
export type ModuleItem = { name: L; desc?: L; meta?: L };
// icon: StepsJourney ikon kataloğundan ad (bkz. STEP_ICONS); yoksa numara.
export type StepItem = { name: L; desc: L; icon?: string };

// Program detay sayfasındaki esnek içerik blokları.
export type Block =
  | { kind: 'text'; title?: L; body: L[] }
  | { kind: 'stats'; title?: L; items: Stat[] }
  | {
      kind: 'features';
      title?: L;
      intro?: L;
      items: FeatureItem[];
      columns?: 1 | 2 | 3 | 4;
    }
  | { kind: 'modules'; title?: L; intro?: L; items: ModuleItem[] }
  | { kind: 'list'; title?: L; intro?: L; items: L[]; ordered?: boolean }
  | { kind: 'steps'; title?: L; intro?: L; items: StepItem[] }
  | { kind: 'chips'; title?: L; intro?: L; items: L[] };

export type Program = {
  slug: string;
  category: CategorySlug;
  group?: L; // alt grup rozeti (örn. Yetenek / Yetkinlik)
  title: L;
  subtitle?: L;
  summary: L; // hem hero açıklaması hem kart özeti
  badge?: L; // küçük vurgu rozeti (örn. MEB Onaylı)
  status: 'open' | 'soon'; // başvurular açık mı, yoksa yakında mı
  applyUrl?: string; // dış bağlantı (yeni sekmede açılır)
  applyTo?: string; // site içi başvuru rotası (ör. /mentorship)
  applyLabel?: L; // başvuru butonu metni (yoksa varsayılan "Hemen Başvur")
  applyDeadline?: L;
  highlights?: Stat[]; // hero'daki hızlı istatistik şeridi
  related?: string[]; // ilgili program slug'ları (detay sayfasında gösterilir)
  comingSoon?: boolean; // sayfa içeriği henüz hazır değil
};

export type Category = {
  slug: CategorySlug;
  title: L;
  subtitle: L;
  comingSoon?: boolean; // kategori sayfası henüz tam dolu değil
};

// NOT: 'collaboration' ve 'events' KATEGORİ SAYFASI YOKTUR (kaldırıldı).
// Programların category alanı bu değerleri taşımaya devam eder; gruplama ve
// filtreleme için kullanılır, ancak /programs/<slug> sayfası üretilmez.
export const CATEGORIES: Category[] = [
  {
    slug: 'development',
    title: {
      tr: 'Kapsül Gelişim Programları',
      en: 'Kapsül Development Programs',
    },
    subtitle: {
      tr: 'Yetenek ve yetkinliği büyüten; teknolojiyi uygulayarak öğreten gelişim programları.',
      en: 'Development programs that grow talent and competency by learning technology hands-on.',
    },
  },
  {
    slug: 'support',
    title: { tr: 'Kapsül Destek Programları', en: 'Kapsül Support Programs' },
    subtitle: {
      tr: 'Yarışma takımlarına atölye, malzeme, mentörlük ve üretim desteği.',
      en: 'Workshop, materials, mentorship and production support for competition teams.',
    },
  },
  {
    slug: 'projects',
    title: { tr: 'Tamamlanan Projeler', en: 'Completed Projects' },
    subtitle: {
      tr: 'Kapsül birimlerinin yürüttüğü akademi, akıllı şehir, proje ve girişimcilik çalışmaları.',
      en: 'Academy, smart-city, project and entrepreneurship work run by Kapsül units.',
    },
  },
  {
    // Odak Alanları — kendi kategorisi olan programlardan DERLENEN temalı
    // vitrin (bkz. FOCUS_SLUGS). Hiçbir programın category'si 'focus' değildir;
    // sayfa bu listeden üretilir.
    slug: 'focus',
    title: { tr: 'Odak Alanları', en: 'Focus Areas' },
    subtitle: {
      tr: 'Temalı iş birliği programları: finans, mühendislik ve tarım teknolojileri.',
      en: 'Themed collaboration programs: finance, engineering and agriculture technologies.',
    },
  },
  {
    slug: 'mentorship',
    title: { tr: 'Mentör Havuzu', en: 'Mentor Pool' },
    subtitle: {
      tr: 'Ekipleri alanında uzman mentörlerle eşleştiren dijital mentorluk sistemi.',
      en: 'A digital mentoring system matching teams with expert mentors.',
    },
  },
];

const ROTA_TECH: Program = {
  slug: 'rota-tech-development',
  category: 'development',
  group: { tr: 'Yetenek', en: 'Talent' },
  title: {
    tr: 'ROTA Gelişim Programı',
    en: 'ROTA Development Program',
  },
  subtitle: {
    tr: 'Teknolojiye sıfırdan, doğru rotayla başlangıç',
    en: 'A guided start into technology, from zero',
  },
  summary: {
    tr: 'Başlangıç seviyesindeki katılımcıların teknoloji okuryazarlığını, algoritmik düşünmeyi ve yapay zekâ–siber güvenlik temellerini kazandığı 13 haftalık bütüncül gelişim programı.',
    en: 'A 13-week holistic program where beginners build technology literacy, algorithmic thinking, and AI–cybersecurity fundamentals.',
  },
  status: 'open',
  applyUrl:
    'https://docs.google.com/forms/d/e/1FAIpQLScE3y6yU3vit2V20fg3Yc4bogy5iaJBaYkTOO-q4vU4MDj7gQ/viewform',
  applyDeadline: {
    tr: 'Son başvuru: 22 Nisan 2026',
    en: 'Deadline: 22 April 2026',
  },
  highlights: [
    { value: '4', label: { tr: 'Modül', en: 'Modules' } },
    { value: '13', label: { tr: 'Hafta', en: 'Weeks' } },
    { value: '119', label: { tr: 'Saat', en: 'Hours' } },
    { value: 'Hafta sonu', label: { tr: 'Uygulama', en: 'Schedule' } },
  ],
};

const TADIMLIK: Program = {
  slug: 'tasting-workshops',
  category: 'development',
  group: { tr: 'Yetenek', en: 'Talent' },
  title: { tr: 'Tadımlık Atölyeler', en: 'Tasting Workshops' },
  subtitle: {
    tr: 'Bir günde dene, üret, kararını ver',
    en: 'Try, build and decide — in a single day',
  },
  summary: {
    tr: 'Teknoloji ve üretim alanlarına hızlı ama etkili bir giriş sunan, gün sonunda somut bir çıktı üretmeyi hedefleyen kısa süreli uygulama atölyeleri.',
    en: 'Short hands-on workshops offering a quick yet effective intro to technology and production, each producing a tangible output by day’s end.',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Başvurular ilerleyen tarihlerde açılacaktır.',
    en: 'Applications will open at a later date.',
  },
  highlights: [
    { value: '9', label: { tr: 'Bağımsız atölye', en: 'Workshops' } },
    {
      value: { tr: '1 gün', en: '1 day' },
      label: { tr: 'Süre', en: 'Duration' },
    },
    {
      value: { tr: 'Somut çıktı', en: 'Tangible output' },
      label: { tr: 'Kazanım', en: 'Outcome' },
    },
  ],
};

const SAVUNMA: Program = {
  slug: 'defense-industry-development',
  category: 'development',
  group: { tr: 'Yetkinlik', en: 'Competency' },
  title: {
    tr: 'Kapsam Profesyonel Gelişim Programı',
    en: 'Kapsam Professional Development Program',
  },
  subtitle: {
    tr: 'Savunma teknolojileri, sistem yaklaşımı ve kurumsal yönetim süreçleri',
    en: 'Defense technologies, systems approach and corporate management processes',
  },
  summary: {
    tr: 'Savunma sanayii ekosisteminin katkılarıyla yürütülen, mühendislik öğrencileri ve genç profesyonellere sektörü uçtan uca tanıtan modüler gelişim programı.',
    en: 'A modular program run with the defense ecosystem, introducing engineering students and young professionals to the sector end-to-end.',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Başvurular ön değerlendirme formu üzerinden alınır.',
    en: 'Applications are taken via a pre-evaluation form.',
  },
  highlights: [
    { value: '2 ay', label: { tr: 'Süre', en: 'Duration' } },
    { value: '14+2', label: { tr: 'Modül', en: 'Modules' } },
    {
      value: { tr: '2 gün', en: '2 days' },
      label: { tr: 'Haftada', en: 'Per week' },
    },
    { value: '3-4 sa', label: { tr: 'Modül başına', en: 'Per module' } },
  ],
};

// Yetkinlik grubu — içerikleri henüz hazır olmayan programlar (skeleton).
const PROTOTIP: Program = {
  slug: 'prototype-acceleration',
  category: 'development',
  group: { tr: 'Yetkinlik', en: 'Competency' },
  title: {
    tr: 'Prototip Hızlandırma Programı',
    en: 'Prototype Acceleration Program',
  },
  subtitle: {
    tr: 'Projeleri Çalışan ve Doğrulanmış Sistemlere Dönüştürün',
    en: 'Turn Projects into Working, Validated Systems',
  },
  summary: {
    tr: 'Teknoloji takımlarının projelerini çalışan, güvenilir ve yarışma koşullarına hazır prototiplere dönüştürmelerini destekleyen teknik gelişim programı.',
    en: 'A technical development program that helps technology teams turn their projects into working, reliable prototypes ready for competition.',
  },
  status: 'open',
};

// ——— İŞ BİRLİĞİ PROGRAMLARI ———

const ENGINEERHUB: Program = {
  slug: 'engineerhub',
  category: 'collaboration',
  title: { tr: 'EngineerHub', en: 'EngineerHub' },
  subtitle: {
    tr: 'Savunma Sanayiine Yönelik Mühendislik Gelişim Programı',
    en: 'An Engineering Development Program for the Defense Industry',
  },
  summary: {
    tr: 'EngineerHub, ASELSAN Konya ve Konya Büyükşehir Belediyesi iş birliğinde, Kapsül Teknoloji Platformu ev sahipliğinde yürütülen savunma sanayii odaklı bir mühendislik gelişim programıdır.',
    en: 'EngineerHub is a defense-industry-focused engineering development program run in partnership with ASELSAN Konya and Konya Metropolitan Municipality, hosted by Kapsül Technology Platform.',
  },
  badge: {
    tr: 'ASELSAN Konya iş birliği',
    en: 'In partnership with ASELSAN Konya',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Başvurular ilerleyen tarihlerde açılacaktır.',
    en: 'Applications will open at a later date.',
  },
  highlights: [
    {
      value: '1.250+',
      label: { tr: 'Toplam başvuru', en: 'Total applications' },
    },
    { value: '50', label: { tr: 'Katılımcı', en: 'Participants' } },
    { value: '12', label: { tr: 'Proje', en: 'Projects' } },
    { value: '3', label: { tr: 'Ortak kurum', en: 'Partners' } },
  ],
};

const BISTLAB: Program = {
  slug: 'bistlab',
  category: 'collaboration',
  title: {
    tr: 'BistLab — Finansal Okuryazarlık',
    en: 'BistLab — Financial Literacy',
  },
  subtitle: {
    tr: 'Borsa İstanbul iş birliğiyle finansal okuryazarlık ve yatırım kültürü',
    en: 'Financial literacy and investment culture with Borsa İstanbul',
  },
  summary: {
    tr: 'Borsa İstanbul Laboratuvarı (BİSTLAB), finansal okuryazarlığı artırmak amacıyla üniversite öğrencileri öncelikli olmak kaydıyla Konya genelinde bilinçli yatırımcılığı teşvik etmek ve yatırım kültürü oluşturmak için çalışmaktadır. Bu amaç doğrultusunda, uzun vadede doğru yatırım tercihleri ve tasarruf bilincini geliştirmeyi hedeflemektedir.',
    en: 'Borsa İstanbul Laboratory (BİSTLAB) works to promote conscious investing and build an investment culture across Konya — prioritising university students — with the aim of increasing financial literacy. In line with this goal, it seeks to develop sound long-term investment choices and savings awareness.',
  },
  badge: { tr: 'Ücretsiz · Sertifikalı', en: 'Free · Certified' },
  status: 'open',
  applyDeadline: {
    tr: 'Eğitimler ücretsizdir; dönemsel gruplar hâlinde açılır.',
    en: 'Training is free and opens in periodic groups.',
  },
  highlights: [
    {
      value: '2.500+',
      label: { tr: 'Finansal okuryazarlık', en: 'Financial literacy' },
    },
    { value: '1.560', label: { tr: 'Eğitim alan kişi', en: 'People trained' } },
    { value: '9', label: { tr: 'BistLab Talks', en: 'BistLab Talks' } },
    {
      value: '13',
      label: { tr: 'Borsa İstanbul stajı', en: 'Borsa İstanbul interns' },
    },
  ],
};

const SU_HACKATHONU: Program = {
  slug: 'water-hackathon',
  category: 'collaboration',
  title: { tr: 'Su Hackathonu', en: 'Water Hackathon' },
  subtitle: {
    tr: 'Akıllı sulama ve su verimliliği',
    en: 'Smart irrigation and water efficiency',
  },
  summary: {
    tr: 'Akıllı sulama ve su verimliliği temasında, su yönetimi ve sürdürülebilirlik için teknoloji temelli çözümlerin üretildiği iki günlük hackathon.',
    en: 'A two-day hackathon producing technology-based solutions for water management and sustainability around smart irrigation and water efficiency.',
  },
  badge: { tr: 'Bakanlık iş birliği', en: 'Ministry partnership' },
  status: 'open',
  applyUrl: 'https://suhackathonu.kapsul.org.tr/',
  applyDeadline: {
    tr: 'Başvurular açık · Katılım ücretsiz',
    en: 'Applications open · Free to join',
  },
  highlights: [
    { value: '50.000₺', label: { tr: 'Birincilik ödülü', en: 'First prize' } },
    {
      value: { tr: '2 gün', en: '2 days' },
      label: { tr: 'Süre', en: 'Duration' },
    },
    { value: '2–4', label: { tr: 'Takım kişi', en: 'Team size' } },
    {
      value: { tr: 'Ücretsiz', en: 'Free' },
      label: { tr: 'Katılım', en: 'Entry' },
    },
  ],
};

const TARIM_IDEATHON: Program = {
  slug: 'agriculture-ideathon',
  category: 'collaboration',
  title: {
    tr: 'Tarım Teknolojileri Ideathonu',
    en: 'Agriculture Technologies Ideathon',
  },
  subtitle: {
    tr: 'Sürdürülebilir tarım için fikir maratonu',
    en: 'An idea marathon for sustainable agriculture',
  },
  summary: {
    tr: 'Kapsül ve Mevlâna Kalkınma Ajansı (MEVKA) iş birliğiyle düzenlenen; sürdürülebilirlik ve ileri teknolojiyle geleceğin tarımına yön veren ideathon. Nisan 2026 edisyonu Zindankale Yerleşkesi’nde gerçekleşti.',
    en: 'An ideathon with Kapsül and Mevlâna Development Agency (MEVKA) shaping the future of agriculture with sustainability and advanced technology. The April 2026 edition was held at the Zindankale campus.',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Nisan 2026 edisyonu tamamlandı; yeni edisyon için bizi takip edin.',
    en: 'The April 2026 edition is complete; follow us for the next one.',
  },
  highlights: [
    { value: '75.000₺', label: { tr: 'Birincilik ödülü', en: 'First prize' } },
    { value: '5', label: { tr: 'Odak alanı', en: 'Focus areas' } },
    { value: '2–4', label: { tr: 'Takım kişi', en: 'Team size' } },
    {
      value: { tr: 'Ücretsiz', en: 'Free' },
      label: { tr: 'Katılım', en: 'Entry' },
    },
  ],
};

const TARIM_CALISTAYI: Program = {
  slug: 'agriculture-workshop',
  category: 'collaboration',
  title: {
    tr: 'Tarım Teknolojileri Çalıştayı',
    en: 'Agriculture Technologies Workshop',
  },
  subtitle: {
    tr: 'Sektör paydaşlarıyla problem belirleme',
    en: 'Problem mapping with sector stakeholders',
  },
  summary: {
    tr: 'Kapsül ve MEVKA iş birliğiyle; bölgedeki tarımsal kısıtları, su sorunlarını ve teknolojik dönüşüm ihtiyaçlarını tüm paydaşlarla masaya yatıran çalıştay. Çıktıları Tarım Ideathonu’nun problem havuzunu oluşturur.',
    en: 'A workshop with Kapsül and MEVKA bringing all stakeholders together on regional agricultural constraints, water issues and transformation needs. Its outputs feed the Agriculture Ideathon problem pool.',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Nisan 2026 edisyonu tamamlandı; yeni çalıştay için bizi takip edin.',
    en: 'The April 2026 edition is complete; follow us for the next one.',
  },
  highlights: [
    { value: '5', label: { tr: 'Odak masası', en: 'Focus tables' } },
    {
      value: '~10',
      label: { tr: 'Masa başı katılımcı', en: 'Per-table participants' },
    },
    {
      value: { tr: 'Açık kaynak', en: 'Open source' },
      label: { tr: 'Çıktı raporu', en: 'Output report' },
    },
  ],
};

const EMLAK_KONUT: Program = {
  slug: 'real-estate-ideathon',
  category: 'collaboration',
  title: { tr: 'Emlak Konut Ideathonu', en: 'Emlak Konut Ideathon' },
  subtitle: {
    tr: 'Akıllı dikey ulaşım & robotik yapı teknolojileri',
    en: 'Smart vertical transport & robotic construction',
  },
  summary: {
    tr: '“Birlikte Düşün, Birlikte Geliştir, Birlikte Deneyimle” mottosuyla; akıllı dikey ulaşım ve robotik yapı teknolojileriyle geleceğin yapılarını şekillendiren fikir maratonu. Emlak Konut GYO ve Anahtar Fikirler iş birliğiyle.',
    en: 'An idea marathon under the motto “Think, Build, Experience Together,” shaping future buildings with smart vertical transport and robotic construction — with Emlak Konut GYO and Anahtar Fikirler.',
  },
  status: 'soon',
  applyDeadline: {
    tr: 'Nisan 2026 edisyonu tamamlandı; yeni edisyon için bizi takip edin.',
    en: 'The April 2026 edition is complete; follow us for the next one.',
  },
  highlights: [
    { value: '100.000₺', label: { tr: 'Birincilik ödülü', en: 'First prize' } },
    { value: '6', label: { tr: 'Odak alanı', en: 'Focus areas' } },
    { value: '18+', label: { tr: 'Yaş', en: 'Age' } },
    {
      value: { tr: 'Ücretsiz', en: 'Free' },
      label: { tr: 'Katılım', en: 'Entry' },
    },
  ],
};

// ——— ETKİNLİKLER ———

// Ekosistem Geliştirme format sayfaları — kurum metniyle BİREBİR: yalnızca
// tanım paragrafı + "Gerçekleştirilen …" listesi (related). Ek başlık, blok
// veya istatistik EKLENMEZ.
const HACKATHON: Program = {
  slug: 'hackathon',
  category: 'events',
  title: { tr: 'Hackathonlar', en: 'Hackathons' },
  summary: {
    tr: 'Hackathonlar, belirlenen bir problem alanına yönelik kısa sürede teknoloji tabanlı çözümler geliştirilmesini amaçlayan yoğun üretim maratonlarıdır. Kapsül Teknoloji Platformu, farklı kurumlarla gerçekleştirdiği iş birlikleri sayesinde düzenlediği hackathonlarla katılımcıları gerçek problemlere çözüm üretmeye teşvik etmekte; fikirlerin uygulanabilir projelere dönüşmesini desteklemektedir.',
    en: 'Hackathons are intensive production marathons aimed at developing technology-based solutions to a defined problem area in a short time. Through its collaborations with a range of institutions, Kapsül Technology Platform encourages participants to produce solutions to real problems in the hackathons it organises, and supports turning ideas into applicable projects.',
  },
  // Başvuru durumu: yeni hackathon duyurulana kadar "Yakında".
  status: 'soon',
  related: ['water-hackathon'],
};

const IDEATHON: Program = {
  slug: 'ideathon',
  category: 'events',
  title: { tr: 'Ideathonlar', en: 'Ideathons' },
  summary: {
    tr: 'Ideathonlar, katılımcıların belirlenen temalar doğrultusunda yenilikçi fikirler geliştirerek bunları uygulanabilir proje önerilerine dönüştürmelerini amaçlayan fikir geliştirme organizasyonlarıdır. Kamu kurumları, üniversiteler ve sektör paydaşlarıyla yürütülen ideathonlar sayesinde katılımcılar, farklı disiplinlerden ekiplerle çalışarak çözüm odaklı düşünme ve proje geliştirme deneyimi kazanmaktadır.',
    en: 'Ideathons are idea-development events where participants develop innovative ideas around set themes and turn them into applicable project proposals. Run together with public institutions, universities and industry stakeholders, ideathons let participants work in cross-disciplinary teams and gain experience in solution-oriented thinking and project development.',
  },
  status: 'open',
  related: ['agriculture-ideathon', 'real-estate-ideathon'],
};

const CALISTAY: Program = {
  slug: 'workshop',
  category: 'events',
  title: { tr: 'Çalıştaylar', en: 'Workshops' },
  summary: {
    tr: 'Çalıştaylar; kamu kurumları, akademisyenler, sektör temsilcileri ve teknoloji geliştiricileri bir araya getirerek ihtiyaçların belirlenmesini, bilgi paylaşımını ve ortak çözüm alanlarının oluşturulmasını amaçlamaktadır. Gerçekleştirilen çalıştaylar, farklı paydaşların deneyimlerini aynı platformda buluşturarak sonraki proje geliştirme süreçlerine yön veren ortak bir bilgi zemini oluşturmaktadır.',
    en: 'Workshops bring together public institutions, academics, industry representatives and technology developers to identify needs, share knowledge and create shared solution areas. The workshops held bring different stakeholders’ experience onto a single platform, forming a common knowledge base that guides subsequent project-development work.',
  },
  status: 'open',
  related: ['agriculture-workshop'],
};

// ——— KAPSÜL DESTEK PROGRAMLARI ———

const TERMINAL_CAGRI: Program = {
  slug: 'terminal-call',
  category: 'support',
  title: { tr: 'Terminal Çağrı Programı', en: 'Terminal Call Program' },
  subtitle: {
    tr: 'Milli Teknoloji Ofisi — yarışma takımlarına uçtan uca destek',
    en: 'National Technology Office — end-to-end support for competition teams',
  },
  summary: {
    tr: 'Konya’da teknoloji odaklı takım ve öğrencilerin potansiyelini ortaya çıkaran; atölye, malzeme, üretim, eğitim ve mentörlükle TEKNOFEST başta olmak üzere yarışmalara hazırlayan Milli Teknoloji Ofisi programı.',
    en: 'The National Technology Office program that unlocks the potential of technology-focused teams in Konya — preparing them for TEKNOFEST and other competitions with workshops, materials, production, training and mentorship.',
  },
  status: 'open',
  applyUrl: 'https://terminal.kapsul.org.tr',
  applyDeadline: {
    tr: 'Yarışma takımı başvuruları her yıl Kasım–Mart döneminde açılır.',
    en: 'Competition team applications open every year between November and March.',
  },
  highlights: [
    { value: '474', label: { tr: 'Takım (2025)', en: 'Teams (2025)' } },
    { value: '17.776', label: { tr: 'Öğrenci (2025)', en: 'Students (2025)' } },
    { value: '18', label: { tr: 'Ödül (2025)', en: 'Awards (2025)' } },
    { value: '63', label: { tr: 'Finalist takım', en: 'Finalist teams' } },
  ],
};

const NEXT_LEVEL: Program = {
  slug: 'next-level',
  category: 'support',
  title: {
    tr: 'Next Level — Sürdürülebilirlik Programı',
    en: 'Next Level — Sustainability Program',
  },
  summary: {
    tr: 'Takımları bir sonraki seviyeye taşıyan sürdürülebilirlik odaklı destek programı. İçerik yakında.',
    en: 'A sustainability-focused support program taking teams to the next level. Content soon.',
  },
  status: 'soon',
  comingSoon: true,
};

// ——— TAMAMLANAN PROJELER ———

const ACADEMY: Program = {
  slug: 'academy',
  category: 'projects',
  title: { tr: 'Akademi', en: 'Academy' },
  subtitle: {
    tr: 'Toplumun teknolojiye erişimini artıran eğitim birimi',
    en: 'An education unit widening public access to technology',
  },
  summary: {
    tr: 'Kapsül Teknoloji Platformu Akademi Birimi’nin misyonu, toplumun teknolojiye erişimini artırmak ve dijital yetkinlikleri geliştirmektir. Bu amaç doğrultusunda, bireylerin ve ekiplerin dijital dönüşüm süreçlerine adapte olabilmeleri ve teknolojiyi etkin bir şekilde kullanabilmeleri için gerekli bilgi ve becerileri kazandırmayı hedeflemektedir.',
    en: 'The mission of the Kapsül Technology Platform Academy Unit is to increase public access to technology and develop digital competencies. To this end, it aims to equip individuals and teams with the knowledge and skills required to adapt to digital transformation and use technology effectively.',
  },
  badge: { tr: 'Ücretsiz · Herkese açık', en: 'Free · Open to all' },
  status: 'open',
  highlights: [
    { value: '10+', label: { tr: 'Eğitim konusu', en: 'Training topics' } },
    {
      value: { tr: 'Ücretsiz', en: 'Free' },
      label: { tr: 'Tüm eğitimler', en: 'All trainings' },
    },
    { value: '5', label: { tr: 'Erasmus+ stajyeri', en: 'Erasmus+ interns' } },
  ],
  related: ['rota-tech-development', 'tasting-workshops'],
};

const AKILLI_SEHIRLER: Program = {
  slug: 'smart-city',
  category: 'projects',
  title: { tr: 'Akıllı Şehirler', en: 'Smart City' },
  subtitle: {
    tr: 'Konya için veri odaklı akıllı şehir laboratuvarı',
    en: 'A data-driven smart-city lab for Konya',
  },
  summary: {
    tr: 'Konya Büyükşehir Belediyesi Kapsül Teknoloji Platformu’nda 2022’de kurulan laboratuvarımızda, Konya’yı sürdürülebilir, yenilikçi ve insan odaklı bir akıllı şehir yapmak için teknoloji kullanarak yaşam kalitesini artırmayı, çevresel sürdürülebilirliği teşvik etmeyi ve vatandaşların günlük yaşamlarını kolaylaştırmayı amaçlıyoruz. Veri analizi yöntemleri kullanarak akıllı şehir dönüşümünü veri odaklı bir yaklaşımla yönlendiriyor ve çeşitli alanlardan toplanan verilerle akıllı çözümler geliştiriyoruz.',
    en: 'In our laboratory founded in 2022 within Konya Metropolitan Municipality’s Kapsül Technology Platform, we aim to make Konya a sustainable, innovative, human-centered smart city — using technology to raise quality of life, promote environmental sustainability and simplify citizens’ daily lives, guiding the transformation with a data-driven approach.',
  },
  status: 'open',
  highlights: [
    { value: '2022', label: { tr: 'Kuruluş', en: 'Founded' } },
    {
      value: { tr: 'Veri odaklı', en: 'Data-driven' },
      label: { tr: 'Yaklaşım', en: 'Approach' },
    },
    { value: 'IoT · YZ', label: { tr: 'Teknolojiler', en: 'Technologies' } },
  ],
};

const PROJE_KAPASITE: Program = {
  slug: 'project-capacity-development',
  category: 'projects',
  title: {
    tr: 'Proje ve Kapasite Geliştirme',
    en: 'Project & Capacity Development',
  },
  subtitle: {
    tr: 'Yerel, ulusal ve uluslararası fonlarla katma değerli projeler',
    en: 'High value-added projects with local, national and international funds',
  },
  summary: {
    tr: 'Kapsül Proje Birimi olarak misyonumuz; yenilikçi, teknoloji ve girişim odaklı projeler üreterek sosyal kalkınmaya katkıda bulunmaktır. Bu doğrultuda, yerel, ulusal ve uluslararası fonlardan maksimum düzeyde faydalanarak katma değeri yüksek projeler geliştirmek ve uygulamak temel hedefimizdir. Gençlerin, birim çalışanlarının ve gönüllülerin profesyonel bilgi, beceri, deneyim ve yetkinliklerine katkıda bulunmanın yanı sıra, uluslararası hareketliliği destekleyerek uluslararası deneyim kazanmalarını sağlamak da misyonumuzun önemli bir parçasıdır.',
    en: 'As the Kapsül Project Unit, our mission is to contribute to social development by producing innovative, technology and entrepreneurship-focused projects — developing high value-added projects by making maximum use of local, national and international funds, and supporting international mobility.',
  },
  status: 'open',
  highlights: [
    {
      value: '13',
      label: { tr: 'Proje başvurusu', en: 'Project applications' },
    },
    { value: '+7', label: { tr: 'Kabul', en: 'Acceptances' } },
    {
      value: '5',
      label: { tr: 'Farklı ülke (staj)', en: 'Countries (internship)' },
    },
    {
      value: '+5',
      label: { tr: 'Uluslararası stajyer', en: 'International interns' },
    },
  ],
  related: ['academy'],
};

const GIRISIMCILIK: Program = {
  slug: 'entrepreneurship',
  category: 'projects',
  title: { tr: 'Girişimcilik', en: 'Entrepreneurship' },
  subtitle: {
    tr: 'Girişimcilik potansiyelini ortaya çıkaran birim',
    en: 'A unit unlocking entrepreneurial potential',
  },
  summary: {
    tr: 'Kapsül Teknoloji Platformu Girişimcilik Birimi, Konya il sınırları içerisinde girişimcilik odaklı çalışmalar yürüten takımların ve öğrencilerin girişimcilik alanındaki potansiyellerini ortaya çıkarmayı ve mevcut potansiyeli geliştirmeyi hedeflemektedir. Bu birimin misyonu, ülkemizin girişimcilik ekosistemini güçlendirmek, yenilikçi ve sürdürülebilir iş modellerinin geliştirilmesini teşvik etmek, nitelikli girişimciler yetiştirmek ve Konya başta olmak üzere ulusal ve uluslararası çapta ekonomik ve sosyal faydalar sağlamaktır.',
    en: 'The Kapsül Entrepreneurship Unit aims to unlock and develop the entrepreneurial potential of teams and students in Konya — strengthening the national entrepreneurship ecosystem, encouraging innovative, sustainable business models and cultivating skilled entrepreneurs.',
  },
  status: 'open',
  highlights: [
    {
      value: { tr: 'Açık Ofis', en: 'Open Office' },
      label: { tr: 'Çalışma alanı', en: 'Workspace' },
    },
    {
      value: { tr: 'Eğitimler', en: 'Trainings' },
      label: { tr: 'Uzman eğitmenler', en: 'Expert trainers' },
    },
    {
      value: { tr: 'Yatırım Ağı', en: 'Investor Network' },
      label: { tr: 'Fon eşleştirme', en: 'Fund matching' },
    },
  ],
  related: ['prototype-acceleration'],
};

// ——— MENTOR HAVUZU ———

const MENTOR_HAVUZU: Program = {
  slug: 'mentor-pool',
  category: 'mentorship',
  title: { tr: 'Mentör Havuzu', en: 'Mentor Pool' },
  subtitle: {
    tr: 'Ekipleri uzman mentörlerle eşleştiren dijital sistem',
    en: 'A digital system matching teams with expert mentors',
  },
  summary: {
    tr: 'Teknik destek, rapor inceleme ve proje doğrulama ihtiyacı olan ekipleri alanında uzman mentörlerle hızlıca eşleştiren merkezi ve dijital mentorluk platformu.',
    en: 'A centralized, digital mentoring platform that quickly matches teams needing technical support, report review or project verification with expert mentors.',
  },
  status: 'open',
  applyTo: '/mentorship',
  applyLabel: { tr: 'Mentörlük Başvurusu', en: 'Apply for Mentorship' },
  applyDeadline: {
    tr: 'Teknik destek veya rapor incelemesi için mentörlük talep formunu doldurun.',
    en: 'Fill in the mentoring request form for technical support or report review.',
  },
  highlights: [
    { value: '51', label: { tr: 'Mentör', en: 'Mentors' } },
    { value: '13', label: { tr: 'Uzmanlık alanı', en: 'Expertise areas' } },
    { value: 'Dijital', label: { tr: 'Süreç', en: 'Process' } },
  ],
};

// ── Tarım Teknolojileri (Odak Alanı) ─────────────────────────────────────────
const TARIM_TEKNOLOJILERI: Program = {
  slug: 'agriculture-technologies',
  category: 'collaboration',
  title: { tr: 'Tarım Teknolojileri', en: 'Agriculture Technologies' },
  subtitle: {
    tr: 'Teknolojiyi tarımsal üretimin gerçek ihtiyaçlarıyla buluşturan odak alanı',
    en: 'A focus area connecting technology with the real needs of agricultural production',
  },
  summary: {
    tr: 'Tarım Teknolojileri odak alanı, teknolojiyi tarımsal üretimin gerçek ihtiyaçlarıyla buluşturarak daha verimli, sürdürülebilir ve yenilikçi çözümler geliştirilmesini destekler.',
    en: 'The Agriculture Technologies focus area connects technology with the real needs of agricultural production, supporting more efficient, sustainable and innovative solutions.',
  },
  status: 'open',
  related: ['agriculture-ideathon', 'agriculture-workshop'],
};

const IS_BIRLIGI: Program[] = [
  ENGINEERHUB,
  TARIM_TEKNOLOJILERI,
  BISTLAB,
  SU_HACKATHONU,
  TARIM_IDEATHON,
  TARIM_CALISTAYI,
  EMLAK_KONUT,
];

const DESTEK: Program[] = [TERMINAL_CAGRI, NEXT_LEVEL];

const PROJELER: Program[] = [
  ACADEMY,
  AKILLI_SEHIRLER,
  PROJE_KAPASITE,
  GIRISIMCILIK,
];

const ETKINLIKLER: Program[] = [HACKATHON, IDEATHON, CALISTAY];

const MENTOR: Program[] = [MENTOR_HAVUZU];

export const PROGRAMS: Program[] = [
  ROTA_TECH,
  TADIMLIK,
  SAVUNMA,
  PROTOTIP,
  ...DESTEK,
  ...IS_BIRLIGI,
  ...PROJELER,
  ...ETKINLIKLER,
  ...MENTOR,
];

export const getCategory = (slug: string): Category | undefined =>
  CATEGORIES.find((c) => c.slug === slug);

export const getProgram = (slug: string): Program | undefined =>
  PROGRAMS.find((p) => p.slug === slug);

// Odak Alanları vitrininde gösterilen programlar (sıra korunur).
export const FOCUS_SLUGS = [
  'bistlab',
  'engineerhub',
  'agriculture-technologies',
];

export const programsOf = (category: CategorySlug): Program[] =>
  // 'focus' bir vitrindir: programlar kendi kategorilerinde kalır, buraya
  // FOCUS_SLUGS sırasıyla derlenir.
  category === 'focus'
    ? (FOCUS_SLUGS.map(getProgram).filter(Boolean) as Program[])
    : PROGRAMS.filter((p) => p.category === category);
